import React, { useState, useRef, forwardRef, useEffect } from 'react';
import dayjs from 'dayjs';
import useControllableState from '../hooks/useControllableState';
import useMergedRef from '../hooks/useMergeRef';
import Calendar from './Calendar';
import BasePicker from './BasePicker';
import { useConfig } from '../ConfigProvider';
import capitalize from '../utils/capitalize';

const DEFAULT_INPUT_FORMAT = 'YYYY-MM-DD';

/**
 * DatePicker component is a date picker component that allows users to select a date from a calendar.
 * It uses the `forwardRef` function to pass the ref to the input element.
 * @param {Object} props - The component props
 * @param {string} [props.className] - The class name of the input
 * @param {boolean} [props.clearable] - The flag to enable the clear button
 * @param {boolean} [props.clearButton] - The flag to enable the clear button
 * @param {boolean} [props.closePickerOnChange] - The flag to close the picker on change
 * @param {number} [props.dateViewCount] - The number of dates to view
 * @param {function} [props.dayClassName] - The function to apply the class name to the day
 * @param {object} [props.dayStyle] - The style to apply to the day
 * @param {Date} [props.defaultMonth] - The default month
 * @param {boolean} [props.defaultOpen] - The flag to open the picker by default
 * @param {Date} [props.defaultValue] - The default value
 * @param {string} [props.defaultView] - The default view
 * @param {boolean} [props.disabled] - The flag to disable the input
 * @param {function} [props.disableDate] - The function to disable the date
 * @param {boolean} [props.enableHeaderLabel] - The flag to enable the header label
 * @param {boolean} [props.disableOutOfMonth] - The flag to disable the out of month dates
 * @param {string} [props.firstDayOfWeek] - The first day of the week
 * @param {boolean} [props.hideOutOfMonthDates] - The flag to hide the out of month dates
 * @param {boolean} [props.hideWeekdays] - The flag to hide the weekdays
 * @param {string} [props.inputFormat] - The input format
 * @param {string} [props.inputPrefix] - The input prefix
 * @param {string} [props.inputSuffix] - The input suffix
 * @param {boolean} [props.inputtable] - The flag to enable the input
 * @param {object} [props.labelFormat] - The label format
 * @param {string} [props.locale] - The locale
 * @param {Date} [props.maxDate] - The maximum date
 * @param {Date} [props.minDate] - The minimum date
 * @param {string} [props.name] - The name of the input
 * @param {function} [props.onBlur] - The function to call when the input is blurred
 * @param {function} [props.onChange] - The function to call when the input changes
 * @param {function} [props.onFocus] - The function to call when the input is focused
 * @param {function} [props.onDropdownClose] - The function to call when the dropdown is closed
 * @param {function} [props.onDropdownOpen] - The function to call when the dropdown is opened
 * @param {boolean} [props.openPickerOnClear] - The flag to open the picker on clear
 * @param {function} [props.renderDay] - The function to render the day
 * @param {string} [props.size] - The size of the input
 * @param {object} [props.style] - The style of the input
 * @param {string} [props.type] - The type of the input
 * @param {Date} [props.value] - The value of the input
 * @param {string[]} [props.weekendDays] - The weekend days
 * @param {object} [props.yearLabelFormat] - The year label format
 * @returns {React.ReactElement}
 */
const DatePicker = forwardRef((props, ref) => {
    const {
        className,
        clearable,
        clearButton,
        closePickerOnChange,
        dateViewCount,
        dayClassName,
        dayStyle,
        defaultMonth,
        defaultOpen,
        defaultValue,
        defaultView,
        disabled,
        disableDate,
        enableHeaderLabel,
        disableOutOfMonth,
        firstDayOfWeek,
        hideOutOfMonthDates,
        hideWeekdays,
        inputFormat,
        inputPrefix,
        inputSuffix,
        inputtable,
        labelFormat,
        locale,
        maxDate,
        minDate,
        name,
        onBlur,
        onChange,
        onFocus,
        onDropdownClose,
        onDropdownOpen,
        openPickerOnClear,
        renderDay,
        size,
        style,
        type,
        value,
        weekendDays,
        yearLabelFormat,
        ...rest
    } = props;

    const { locale: themeLocale } = useConfig();

    const finalLocale = locale || themeLocale;

    const dateFormat =
        type === 'date'
            ? DEFAULT_INPUT_FORMAT
            : inputFormat || DEFAULT_INPUT_FORMAT;

    const [dropdownOpened, setDropdownOpened] = useState(defaultOpen);

    const inputRef = useRef();

    const [lastValidValue, setLastValidValue] = useState(defaultValue ?? null);

    const [_value, setValue] = useControllableState({
        prop: value,
        defaultProp: defaultValue,
        onChange,
    });

    const [calendarMonth, setCalendarMonth] = useState(
        _value || defaultMonth || new Date()
    );

    const [focused, setFocused] = useState(false);

    const [inputState, setInputState] = useState(
        _value instanceof Date
            ? capitalize(dayjs(_value).locale(finalLocale).format(dateFormat))
            : ''
    );

    const closeDropdown = () => {
        setDropdownOpened(false);
        onDropdownClose?.();
    };

    const openDropdown = () => {
        setDropdownOpened(true);
        onDropdownOpen?.();
    };

    useEffect(() => {
        if (value === null && !focused) {
            setInputState('');
        }

        if (value instanceof Date && !focused) {
            setInputState(
                capitalize(dayjs(value).locale(finalLocale).format(dateFormat))
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, focused, themeLocale]);

    useEffect(() => {
        if (defaultValue instanceof Date && inputState && !focused) {
            setInputState(
                capitalize(dayjs(_value).locale(finalLocale).format(dateFormat))
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [themeLocale]);

    const handleValueChange = (date) => {
        setValue(date);
        setInputState(
            capitalize(dayjs(date).locale(finalLocale).format(dateFormat))
        );
        closePickerOnChange && closeDropdown();
        window.setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleClear = () => {
        setValue(null);
        setLastValidValue(null);
        setInputState('');
        openPickerOnClear && openDropdown();
        inputRef.current?.focus();
    };

    const parseDate = (date) => dayjs(date, dateFormat, finalLocale).toDate();

    const setDateFromInput = () => {
        let date = typeof _value === 'string' ? parseDate(_value) : _value;

        if (maxDate && dayjs(date).isAfter(maxDate)) {
            date = maxDate;
        }

        if (minDate && dayjs(date).isBefore(minDate)) {
            date = minDate;
        }

        if (dayjs(date).isValid()) {
            setValue(date);
            setLastValidValue(date);
            setInputState(
                capitalize(dayjs(date).locale(finalLocale).format(dateFormat))
            );
            setCalendarMonth(date);
        } else {
            setValue(lastValidValue);
        }
    };

    const handleInputBlur = (event) => {
        typeof onBlur === 'function' && onBlur(event);
        setFocused(false);

        if (inputtable) {
            setDateFromInput();
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && inputtable) {
            closeDropdown();
            setDateFromInput();
        }
    };

    const handleInputFocus = (event) => {
        typeof onFocus === 'function' && onFocus(event);
        setFocused(true);
    };

    const handleChange = (event) => {
        openDropdown();

        const date = parseDate(event.target.value);
        if (dayjs(date).isValid()) {
            setValue(date);
            setLastValidValue(date);
            setInputState(event.target.value);
            setCalendarMonth(date);
        } else {
            setInputState(event.target.value);
        }
    };

    return (
        <BasePicker
            inputtable={inputtable}
            dropdownOpened={dropdownOpened}
            setDropdownOpened={setDropdownOpened}
            ref={useMergedRef(ref, inputRef)}
            size={size}
            style={style}
            className={className}
            onChange={handleChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            name={name}
            inputLabel={inputState}
            clearable={
                type === 'date' ? false : clearable && !!_value && !disabled
            }
            clearButton={clearButton}
            onClear={handleClear}
            disabled={disabled}
            onDropdownClose={onDropdownClose}
            onDropdownOpen={onDropdownOpen}
            type={type}
            inputPrefix={inputPrefix}
            inputSuffix={inputSuffix}
            {...rest}
        >
            <Calendar
                locale={finalLocale}
                month={inputtable ? calendarMonth : undefined}
                defaultMonth={
                    defaultMonth ||
                    (_value instanceof Date ? _value : new Date())
                }
                onMonthChange={setCalendarMonth}
                value={
                    _value instanceof Date
                        ? _value
                        : _value && dayjs(_value).toDate()
                }
                onChange={handleValueChange}
                labelFormat={labelFormat}
                dayClassName={dayClassName}
                dayStyle={dayStyle}
                disableOutOfMonth={disableOutOfMonth}
                minDate={minDate}
                maxDate={maxDate}
                disableDate={disableDate}
                firstDayOfWeek={firstDayOfWeek}
                preventFocus={inputtable}
                dateViewCount={dateViewCount}
                enableHeaderLabel={enableHeaderLabel}
                defaultView={defaultView}
                hideOutOfMonthDates={hideOutOfMonthDates}
                hideWeekdays={hideWeekdays}
                renderDay={renderDay}
                weekendDays={weekendDays}
                yearLabelFormat={yearLabelFormat}
            />
        </BasePicker>
    );
});

DatePicker.defaultProps = {
    closePickerOnChange: true,
    labelFormat: {
        month: 'MMM',
        year: 'YYYY',
    },
    defaultOpen: false,
    name: 'date',
    clearable: true,
    disabled: false,
    firstDayOfWeek: 'monday',
    openPickerOnClear: false,
};

export default DatePicker;
